// profile-controller.ts — the standalone MY PROFILE screen, LOGIC half (pure TS, no DOM). The main
// menu's MY PROFILE button opens this screen; it shows the CURRENT player's own profile (the SAME
// detail the LEADERBOARD modal renders), fetched from the wi-api worker's /stats/profile.json.
//
// Two entry states (start()):
//   • signed in (currentName() non-empty) → fetch + show the profile straight away.
//   • not signed in → a named-login form (no anonymous option). On a successful sign-in the player's
//     identity is persisted (the host's signIn seam) so they're signed in app-wide, then the profile
//     loads. The view (profile-view.ts) renders this state; the whole controller is node-testable with
//     scripted httpGet + signIn seams, mirroring leaderboard-controller.ts.

import type { LeaderProfile } from './leaderboard-controller.ts';
import { queryEncode } from '../ui/loginhandler.ts';
import { openReplayViewer } from '../ui/replaylink.ts';

/** login → the named-login form · loading → profile fetch in flight · ready → profile shown ·
 *  empty → signed in but no profile yet (no MP games) · error → fetch failed. */
export type ProfileMode = 'login' | 'loading' | 'ready' | 'empty' | 'error';

/** The host's sign-in result. 'ok' → identity validated + persisted (the player is now signed in);
 *  'nopassword' → a name was given with no password; 'fail' → bad credentials / service down. A string
 *  discriminant (not a boolean) so it narrows under this tree's strictNullChecks-off typecheck. */
export type ProfileSignInResult =
  { kind: 'ok'; name: string } | { kind: 'nopassword' } | { kind: 'fail' };

export interface ProfileDeps {
  /** GET seam (the SAME shape as the leaderboard / lobby httpGet) — production: fetch; tests: scripted. */
  httpGet(url: string): Promise<{ body: string | null; error?: string }>;
  /** base of the JSON endpoints, e.g. 'https://api.warfareincorporated.com/stats' (no trailing slash). */
  baseUrl: string;
  /** the view re-renders on every state change. */
  onChange(): void;
  /** the current non-anonymous stats username ('' when not signed in) — Shell.cpp StatsUsername. */
  currentName(): string;
  /** validate name+password against the auth server and persist the identity on success
   *  (LoginHandler.GetToken + SaveToGlobals, minus the lobby/transport). */
  signIn(name: string, password: string): Promise<ProfileSignInResult>;
  /** clear the persisted identity (token + password) so the app is signed out everywhere — the
   *  leaderboard pin goes dark, MP stops auto-authenticating, and this gate returns to the sign-in
   *  form. The username is kept so the form pre-fills for a quick re-sign-in. */
  signOut?(): void;
  /** open an external URL — the login REGISTER / FORGOT ACCOUNT pages, and the replay viewer. */
  openUrl?(url: string): void;
}

export class ProfileController {
  mode: ProfileMode = 'login';
  profile: LeaderProfile | null = null;
  /** the name whose profile is shown (the title bar) — the signed-in name. */
  name = '';
  /** login-form fields + a transient status line (sign-in errors / "Signing in…"). */
  loginName = '';
  loginPassword = '';
  loginStatus = '';

  private readonly d: ProfileDeps;
  // a monotonic token so a slow earlier fetch can't clobber a newer one (re-entry / re-login).
  private epoch = 0;

  constructor(deps: ProfileDeps) {
    this.d = deps;
  }

  /** Open the screen: signed in → load the profile; otherwise → the login form, pre-filled with the
   *  remembered username (so a signed-out-but-known player just re-enters their password). */
  start(): void {
    const me = this.d.currentName().trim();
    if (me.length > 0) {
      void this.load(me);
      return;
    }
    this.mode = 'login';
    this.loginName = this.loginName || '';
    this.loginStatus = '';
    this.d.onChange();
  }

  setLoginName(v: string): void {
    this.loginName = v;
  }
  setLoginPassword(v: string): void {
    this.loginPassword = v;
  }

  /** LOGIN: validate + persist the credentials, then load the profile. Failures stay on the form. */
  async submitLogin(): Promise<void> {
    const name = this.loginName.trim();
    if (name.length === 0) {
      this.loginStatus = 'Enter your player name.';
      this.d.onChange();
      return;
    }
    this.loginStatus = 'Signing in…';
    this.d.onChange();
    const r = await this.d.signIn(name, this.loginPassword);
    if (r.kind === 'ok') {
      this.loginPassword = '';
      void this.load(r.name);
      return;
    }
    this.loginStatus =
      r.kind === 'nopassword'
        ? 'Enter your password.'
        : 'Sign-in failed — check your name and password.';
    this.d.onChange();
  }

  /** Fetch the player's full profile. A 404 (no player row → never played MP) is the friendly
   *  'empty' state, NOT an error; the wi-api worker returns 404 {"error":"not found"} for an unknown
   *  player (handlers.ts profileJson), and the httpGet seam surfaces that as error "Server returned 404". */
  private async load(name: string): Promise<void> {
    const epoch = ++this.epoch;
    this.name = name;
    this.profile = null;
    this.mode = 'loading';
    this.d.onChange();
    const req = await this.d.httpGet(`${this.d.baseUrl}/profile.json?u=${queryEncode(name)}`);
    if (epoch !== this.epoch) return; // a newer request superseded this one
    if (req.body === null) {
      this.mode = is404(req.error) ? 'empty' : 'error';
      this.d.onChange();
      return;
    }
    try {
      const obj = JSON.parse(req.body) as LeaderProfile & { error?: string };
      if (obj.error) {
        this.mode = obj.error === 'not found' ? 'empty' : 'error';
      } else {
        this.profile = obj;
        this.mode = 'ready';
      }
    } catch {
      this.mode = 'error';
    }
    this.d.onChange();
  }

  /** SIGN OUT (from the profile modal): clear the persisted credentials and return to the sign-in
   *  form, pre-filled with the just-signed-out name so a re-sign-in only needs the password. The
   *  app is now signed out app-wide (currentName goes empty: the leaderboard pin clears and MP no
   *  longer auto-authenticates). */
  signOut(): void {
    this.d.signOut?.();
    this.loginName = this.name;
    this.loginPassword = '';
    this.loginStatus = '';
    this.profile = null;
    this.name = '';
    this.mode = 'login';
    this.d.onChange();
  }

  /** Open the in-browser replay viewer for one of the profile's recent games (no-op without a key). */
  watchReplay(replayKey: string): void {
    openReplayViewer(replayKey, this.d.openUrl);
  }
}

/** True when an httpGet error string reports an HTTP 404 (the worker's not-found response). */
function is404(error: string | undefined): boolean {
  return error !== undefined && /\b404\b/.test(error);
}
